import cx from 'classnames';
import { formatDistanceToNow } from 'date-fns';
import * as _ from 'lodash';
import { useEffect, useRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import nuc from '../../../../../shared/utils/nuc';
import BulkSelectionBanner from '../../../generic/BulkSelectionBanner';
import Button from '../../../generic/Button/Button';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import SearchInput from '../../../generic/Forms/SearchInput';
import { icons } from '../../../generic/Icon/icons';
import IconText from '../../../generic/Icon/IconText';
import Link from '../../../generic/Link/Link';
import RouterLink from '../../../generic/Link/RouterLink';
import List from '../../../generic/List';
import PaginationFooter from '../../../generic/Pagination/PaginationFooter';
import TooltipIcon from '../../../generic/TooltipIcon';
import { FossabotPrsSortMenu } from '../../fossabotPrs/FossabotPrsSortMenu';
import BranchDropdown from '../BranchDropdown';
import { classifyRepo } from '../repoState';
import ServiceFiltersMenu from '../ServiceFiltersMenu';
import type { FossabotPRSort } from '../../../../../shared/fossabot/common';
import type { Filter, Repository, ServiceFilter } from '../../../../types';
import type { OnBranchChangeFn, OnSelectRepositoryFn } from '../HybridProjectsTable/types';

import styles from './VcsRepositoryList.module.less';

/** Page sizes offered by the footer, matching what the legacy import pagination offered. */
const COUNT_OPTIONS = [5, 10, 20];

/** Placeholder rows shown while a page is in flight. */
const LOADING_ROW_KEYS = ['first', 'second', 'third'];

/**
 * A repo with no branches has nothing importable, and one already connected to a project has
 * nothing left to import, so neither can be selected. Shared with the page so "select all"
 * across pages picks the same rows the checkboxes offer.
 */
export const isRepoSelectable = (repo: Repository): boolean =>
  !_.isEmpty(repo.branches) && classifyRepo(repo) !== 'connected';

type SelectPageCheckboxProps = {
  isChecked: boolean;
  isIndeterminate: boolean;
  onChange: () => void;
};

/** The header's select-all-on-page checkbox. Native, like the row checkboxes; indeterminate is DOM-only. */
const SelectPageCheckbox: VoidFunctionComponent<SelectPageCheckboxProps> = ({
  isChecked,
  isIndeterminate,
  onChange,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);
  return (
    <input
      ref={ref}
      className={styles.checkbox}
      type="checkbox"
      aria-label="Select all repositories on this page"
      checked={isChecked}
      onChange={onChange}
    />
  );
};

type RowCheckboxProps = {
  repo: Repository;
  isSelected: boolean;
  isSelectable: boolean;
  isConnected: boolean;
  onSelectRepository: OnSelectRepositoryFn;
};

/**
 * Every row gets a checkbox so the titles line up. An already-imported repo's is checked and
 * disabled, to read as "done"; a repo with nothing importable is disabled and empty.
 */
const RowCheckbox: VoidFunctionComponent<RowCheckboxProps> = ({
  repo,
  isSelected,
  isSelectable,
  isConnected,
  onSelectRepository,
}) => (
  <input
    className={styles.checkbox}
    type="checkbox"
    aria-label={`Select ${repo.title}`}
    checked={isSelected || isConnected}
    disabled={!isSelectable}
    onChange={() => onSelectRepository(repo)}
    // The row toggles on click too; without this a checkbox click would toggle twice.
    onClick={(event) => event.stopPropagation()}
  />
);

type RepositoryRowProps = {
  repo: Repository;
  isSelected: boolean;
  onSelectRepository: OnSelectRepositoryFn;
  onBranchChange: OnBranchChangeFn;
};

/**
 * One repository, laid out like a GitHub repo card: title with private / fork markers, the
 * description, then when it was last pushed. A repo with no branches has nothing importable,
 * so its row is disabled; one already connected to a project shows that instead.
 *
 * Clicking anywhere on a selectable row toggles it. The title link, checkbox and branch
 * dropdown stop the click so they keep their own behaviour.
 */
const RepositoryRow: VoidFunctionComponent<RepositoryRowProps> = ({
  repo,
  isSelected,
  onSelectRepository,
  onBranchChange,
}) => {
  const hasNoBranches = _.isEmpty(repo.branches);
  const isConnected = classifyRepo(repo) === 'connected';
  const isSelectable = isRepoSelectable(repo);
  const projectLocator = isConnected ? repo.project?.locator ?? repo.connectedProjects?.[0] : null;

  return (
    <List.RowContainer isDisabled={hasNoBranches}>
      <List.Row
        hasCheckbox
        isSelected={isSelected}
        isHighlighted={isConnected}
        isInteractive={isSelectable}
        onClick={isSelectable ? () => onSelectRepository(repo) : undefined}
        testId="vcs-import-repository-row"
        left={(
          <div className={styles.checkboxCell}>
            <RowCheckbox
              repo={repo}
              isSelected={isSelected}
              isSelectable={isSelectable}
              isConnected={isConnected}
              onSelectRepository={onSelectRepository}
            />
          </div>
        )}
        // Presentation-only wrapper: keeps the dropdown's clicks from toggling the row.
        right={isSelectable && (
          <div className={styles.rowActions} role="presentation" onClick={(event) => event.stopPropagation()}>
            <BranchDropdown repo={repo} onBranchChange={onBranchChange} />
          </div>
        )}
      >
        <div className={styles.titleLine}>
          <Link className={styles.title} to={repo.url as string} openInNewTab shouldStopPropagation>{repo.title}</Link>
          {repo.isPrivate && <TooltipIcon id={`${repo.title}-private`} icon={icons.lock} tooltip="Private repo" />}
          {repo.isFork && <TooltipIcon id={`${repo.title}-fork`} icon={icons.codeFork} tooltip="Forked repo" />}
          {repo.updated && (
            <span className={styles.updated}>
              {`Updated ${formatDistanceToNow(new Date(repo.updated), { addSuffix: true })}`}
            </span>
          )}
        </div>
        {repo.description ? (
          <div className={styles.description} title={repo.description}>{repo.description}</div>
        ) : (
          <div className={cx(styles.description, styles.noDescription)}>No description provided</div>
        )}
        {projectLocator && (
          <div className={styles.imported}>
            <IconText icon={icons.checkCircle} text="Imported" />
            <span aria-hidden>&middot;</span>
            <RouterLink to={`/projects/${nuc(projectLocator)}`} className={styles.projectLink}>View Project</RouterLink>
          </div>
        )}
      </List.Row>
    </List.RowContainer>
  );
};

type Props = {
  repositories: Repository[];
  selectedRepos: Repository[];
  /** True once "select all" has enumerated every repo on the account into the selection. */
  areAllSelected: boolean;
  /** True while "select all" is enumerating the account. */
  isSelectingAll: boolean;
  /** The active account's name, read out by the selection banner. */
  accountName: string;
  /** Connected accounts, grouped into filter categories by type; the menu also hosts "Install". */
  filters: ServiceFilter[];
  selectedFilter?: Filter;
  searchValue: string;
  /** The order the rows are in (by last push), which the sort control reads out. */
  sort: FossabotPRSort;
  page: number;
  pageSize: number;
  /** Total repositories on the account, held by the page across pages the API reports none for. */
  totalCount: number;
  isLoading: boolean;
  /** Message for a failed listing; null when the listing is fine. */
  error: string | null;
  /** True while an import or the teams list is in flight, which disables both import buttons. */
  isImportBusy: boolean;
  onSearchChange: (value: string) => void;
  onSortChange: (sort: FossabotPRSort) => void;
  onSelectAccount: (filterType: string, connectionId: string) => Promise<void>;
  onInstallGithubApp: () => void;
  onSetPage: (page: number) => void;
  onSetPageSize: (pageSize: number) => void;
  onSelectRepository: OnSelectRepositoryFn;
  /** Adds or removes every given repo from the selection at once (the header checkbox). */
  onSelectRepositories: (repos: Repository[], isSelected: boolean) => void;
  /** Selects every repo on the account, across pages. */
  onSelectAll: () => void;
  onClearSelection: () => void;
  onBranchChange: OnBranchChangeFn;
  onImportSelected: () => void;
  onImportAll: () => void;
};

/**
 * The main panel once fossabot is installed: the account picker and search over one
 * account's repositories, the repositories themselves, and the controls that import them.
 *
 * Built from the generic List / SearchInput / PaginationFooter pieces the fossabot PRs page
 * uses rather than the legacy import table, so the two pages read as one product.
 */
export const VcsRepositoryList: VoidFunctionComponent<Props> = ({
  repositories,
  selectedRepos,
  areAllSelected,
  isSelectingAll,
  accountName,
  filters,
  selectedFilter,
  searchValue,
  sort,
  page,
  pageSize,
  totalCount,
  isLoading,
  error,
  isImportBusy,
  onSearchChange,
  onSortChange,
  onSelectAccount,
  onInstallGithubApp,
  onSetPage,
  onSetPageSize,
  onSelectRepository,
  onSelectRepositories,
  onSelectAll,
  onClearSelection,
  onBranchChange,
  onImportSelected,
  onImportAll,
}) => {
  // `locator` is the repository identity here: `title` is only the bare repo name.
  const selectedLocators = new Set(selectedRepos.map((repo) => repo.locator));
  const selectableOnPage = repositories.filter(isRepoSelectable);
  const selectedOnPageCount = selectableOnPage.filter((repo) => selectedLocators.has(repo.locator)).length;
  const isPageSelected = selectableOnPage.length > 0 && selectedOnPageCount === selectableOnPage.length;
  const isPagePartiallySelected = selectedOnPageCount > 0 && !isPageSelected;

  return (
    <div className={styles.panel}>
      <div className={styles.toolbar}>
        {filters.length > 0 && (
          <ServiceFiltersMenu
            categories={filters}
            selectedFilter={selectedFilter}
            onSelect={onSelectAccount}
            onInstall={onInstallGithubApp}
          />
        )}
        <div className={styles.search}>
          <SearchInput
            value={searchValue}
            // SearchInput hands back the change event too; the page only wants the value.
            onChange={(value) => onSearchChange(value)}
            size="small"
            label="Search repos"
            placeholder="Search repos"
            testId="vcs-import-search"
          />
        </div>
        <Button
          type={BUTTON_TYPE.SECONDARY}
          size={BUTTON_SIZE.MEDIUM}
          icon={icons.download}
          isDisabled={isImportBusy}
          onClick={onImportAll}
        >
          Import all
        </Button>
        <Button
          size={BUTTON_SIZE.MEDIUM}
          isDisabled={selectedRepos.length === 0 || isImportBusy}
          onClick={onImportSelected}
        >
          {selectedRepos.length > 0 ? `Import ${selectedRepos.length} Selected` : 'Import Selected'}
        </Button>
      </div>

      {error && <ErrorAlert status={{ error: `Error loading repositories: ${error}` }} withMargin />}

      <List>
        <List.Header hasCheckbox>
          <SelectPageCheckbox
            isChecked={isPageSelected}
            isIndeterminate={isPagePartiallySelected}
            onChange={() => onSelectRepositories(selectableOnPage, !isPageSelected)}
          />
          {/* Same Newest / Oldest control as the fossabot PRs page; the header pushes it right. */}
          <FossabotPrsSortMenu sort={sort} onSortChange={onSortChange} label="Last Updated" />
        </List.Header>
        <List.Body>
          {!isLoading && (isSelectingAll || isPageSelected || areAllSelected) && (
            <BulkSelectionBanner
              loadingMessage={isSelectingAll ? `Selecting all repos in ${accountName}...` : undefined}
              itemsNoun="repo"
              pageSize={selectableOnPage.length}
              // The API total counts imported repos too; once enumerated, the selection is the real count.
              totalItemsCount={areAllSelected ? selectedRepos.length : totalCount}
              areAllItemsSelected={areAllSelected}
              sectionName={accountName}
              onSelectAll={onSelectAll}
              onClearSelection={onClearSelection}
            />
          )}
          {isLoading && LOADING_ROW_KEYS.map((key) => <List.RowLoading key={key} isInteractive={false} />)}
          {!isLoading && !error && repositories.length === 0 && (
            <div className={styles.empty} role="status">No repositories found</div>
          )}
          {!isLoading && repositories.map((repo) => (
            <RepositoryRow
              key={repo.locator ?? repo.title}
              repo={repo}
              isSelected={selectedLocators.has(repo.locator)}
              onSelectRepository={onSelectRepository}
              onBranchChange={onBranchChange}
            />
          ))}
        </List.Body>
      </List>

      <PaginationFooter
        page={page}
        count={pageSize}
        totalItems={totalCount}
        setPage={onSetPage}
        setCount={onSetPageSize}
        countOptions={COUNT_OPTIONS}
      />
    </div>
  );
};
